/**
 * @fileoverview Email verification component
 * 
 * This file implements the email verification page for the leader profile application.
 * It allows users to verify their email address by providing the email associated with
 * their invitation and validating it against a token provided in the URL.
 * 
 * Key features:
 * - Email validation with error handling
 * - Token-based verification process
 * - Confirmation modal for user feedback
 * - Unauthorized access handling
 * - Client-specific branding
 */

import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  ButtonVariant,
  IconName,
  ButtonIconAlign,
  TextInputWidth,
  TextInput,
  TextInputSize,
  TextInputIconAlign,
  Icon,
} from "@eightfold.ai/octuple";
import Header from "../commons/Header";
import { actionButtonStyle } from "../commons/commonStyles";
import Footer from "../commons/Footer";
import Api from "../Api";
import { globalContext } from "../store";
import { useNavigate } from "react-router";
import { getOnboardingImg, getHeaderLogo, chkForError } from "../commons/commonFunctions";
import { useSearchParams } from "react-router-dom";
import { schema } from "../ValidationSchema";

/**
 * Email verification component
 *
 * This component allows users to verify their email address by entering it and
 * confirming it with a token provided in the URL. If verification is successful,
 * users are redirected to the welcome page. If the token is missing or invalid,
 * an unauthorized access message is displayed.
 *
 * @returns {JSX.Element} The rendered email verification page or unauthorized message
 */
const SetPassword = () => {
  // Get token from URL query parameters
  let [params] = useSearchParams();
  let navigate = useNavigate();
  let token = params?.get("token");

  // Access global state for client information
  const { globalState } = useContext(globalContext);

  // State variables for component
  const [password, setPassword] = useState<any>("");
  const [confirmPassword, setConfirmPassword] = useState<any>("");
  const [unauthorized, setUnauthorized] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [errorMsg, setErrorMsg] = useState({
    password: "",
    confirmPassword: "",
    apiError: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [disableSubmitBtn, setDisableSubmitBtn] = useState(false);

  // Get client information for branding
  const title = globalState?.clientData?.name;

  /**
   * Check if token exists on component mount
   * If token is missing, show unauthorized message
   */
  useEffect(() => {
    if (!token) {
      setUnauthorized(true);
    }
  }, [token]);

  /**
   * Validates the email address and shows confirmation modal if valid
   *
   * This function:
   * 1. Shows a loader during validation
   * 2. Validates the email format using the validation schema
   * 3. Shows an error message if validation fails
   * 4. Shows a confirmation modal if email is valid
   */
  const confirmEmail = () => {
    setShowLoader(true);
    // Trim whitespace from password fields before validation
    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();
    const data = {
      password: trimmedPassword,
      confirmPassword: trimmedConfirmPassword,
    };

    const validationResult = schema.validate(data);
    const msg = validationResult?.error?.details[0].message;
    const keyMap = validationResult?.error?.details[0].path[0];
    let errorMsgTxt: any = { [`${keyMap}`]: msg };
    if (msg) {
      setErrorMsg(errorMsgTxt);
      setShowLoader(false);
    } else {
      setErrorMsg({ password: "", confirmPassword: "", apiError: false });
      const payload = {
        password: trimmedPassword,
      };

      Api.setPassword(payload, token)
        .then((response: any) => {
          if (response?.success) {
            navigate("/welcome");
          } else {
            // Handle error responses from API
            setShowLoader(false);
            const msg =
              response?.data?.detail ||
              "Failed to set password. Please try again.";
            errorMsgTxt = { confirmPassword: msg, password: msg, apiError: true};
            setErrorMsg(errorMsgTxt);
          }
        })
        .catch((err: any) => {
          console.log("err", err);
        });
    }
  };

  const validations = {
    length: password.length >= 16,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    specialChar: /[!\"\'#$%&’()*+,-./:;<=>?@[\]^_\`{|}~]/.test(password), //NOSONAR - Special characters allowed in password
    space: /^\S+$/.test(password),
  };

  const DisableSubmit = () => {
    const isValid =
      Object.values(validations).every((value) => value === true) &&
      password === confirmPassword;
    setDisableSubmitBtn(!isValid);
    setErrorMsg((prev: any) => ({
      ...prev,
      apiError: false,
    }));
  };

  React.useEffect(() => {
    DisableSubmit();
    setErrorMsg((prev: any) => ({
      ...prev,
      password: "",
      confirmPassword: confirmPassword.length ? "Passwords do not match" : "",
    }));
  }, [password, confirmPassword]);

  React.useEffect(() => {
    if (password === confirmPassword) {
      setErrorMsg((prev: any) => ({
        ...prev,
        password: "",
        confirmPassword: "",
      }));
    } else {
      setErrorMsg((prev: any) => ({
        ...prev,
        confirmPassword: confirmPassword.length ? "Passwords do not match" : "",
      }));
    }
    DisableSubmit();
  }, [confirmPassword]);

  const checkIcon = () => {
    return <Icon path={IconName.mdiCheck} color={"#35c20c"} />;
  };

  const closeIcon = () => {
    return <Icon path={IconName.mdiClose} color={"#ff0000"} />;
  };

  const chkPasswordFieldType = (condition: boolean) => {
    return condition ? "text" : "password";
  }

  const chkPasswordFieldIcon = (condition: boolean) => {
    return condition ? IconName.mdiEyeOutline : IconName.mdiEyeOffOutline;
  };

  const chkValidation = (condition: boolean) => {
    return condition ? "valid" : "invalid";
  };

  return (
    <>
      {/* Show unauthorized message if token is missing */}
      {unauthorized ? (
        <h1 className="inviteVerifyError">Unauthorized Access</h1>
      ) : (
        <div
          className="mainWrapperOnboarding"
          style={{ backgroundImage: `url(${getOnboardingImg(globalState)})` }}
        >
          <div className="middleContentWrapper">
            {/* Header with client logo */}
            <Header title={title} headerLogo={getHeaderLogo(globalState)} />
            <hr className="headerSeparator" />

            {/* Welcome content section */}
            <div style={{ alignItems: "unset" }}>
              <div
                className="contentWrapper"
                style={{ alignItems: "unset", padding: "32px 40px" }}
              >
                <h2 style={{ marginBottom: "24px" }}>Set up your password</h2>
                <div className="fieldWrap">
                  <TextInput
                    htmlType={chkPasswordFieldType(showPassword)}
                    id="password"
                    clearable={false}
                    maxlength={100}
                    minlength={0}
                    name="password"
                    labelProps={{
                      text: "Password",
                      classNames: "labelText",
                    }}
                    inputWidth={TextInputWidth.fill}
                    classNames="inputTextField"
                    size={TextInputSize.Large}
                    iconButtonProps={{
                      iconProps: {
                        path: chkPasswordFieldIcon(showPassword),
                      },
                      onClick: () => {
                        setShowPassword(!showPassword);
                      },
                    }}
                    onChange={(e: any) => {
                      setPassword(e.target.value);
                    }}
                    alignIcon={TextInputIconAlign.Right}
                    status={chkForError(errorMsg?.password)}
                  />
                  {errorMsg?.password && (
                    <span className="errorMessage">{errorMsg?.password}</span>
                  )}

                  <ul className="validation-list">
                    <li className={chkValidation(validations.length)}>
                      {validations.length ? checkIcon() : closeIcon()}
                      <span className="right"> At least 16 characters</span>
                    </li>
                    <li className={chkValidation(validations.uppercase)}>
                      {validations.uppercase ? checkIcon() : closeIcon()}
                      <span className="right">
                        {" "}
                        At least one uppercase letter
                      </span>
                    </li>
                    <li className={chkValidation(validations.lowercase)}>
                      {validations.lowercase ? checkIcon() : closeIcon()}
                      <span className="right">
                        {" "}
                        At least one lowercase letter
                      </span>
                    </li>
                    <li className={chkValidation(validations.number)}>
                      {validations.number ? checkIcon() : closeIcon()}
                      <span className="right"> At least one number</span>
                    </li>
                    <li className={chkValidation(validations.specialChar)}>
                      {validations.specialChar ? checkIcon() : closeIcon()}
                      <span className="right">
                        {" "}
                        {
                          'At least one special character (e.g. !"#$%&’()*+,-./:;<=>?@[]^_`{|}~)'
                        }
                      </span>
                    </li>
                    <li className={chkValidation(validations.space)}>
                      {validations.space ? checkIcon() : closeIcon()}
                      <span className="right"> {"No spaces allowed"}</span>
                    </li>
                    
                    <li className={chkValidation(validations.space)}>
                      <span>{errorMsg?.apiError && closeIcon()}</span>
                      <span className="right">Cannot contain first or last name or parts of email address</span>
                    </li>
                  </ul>
                </div>
                <div className="fieldWrap">
                  <TextInput
                    htmlType={chkPasswordFieldType(showConfirmPassword)}
                    id="confirmPassword"
                    clearable={false}
                    maxlength={100}
                    minlength={0}
                    name="confirmPassword"
                    labelProps={{
                      text: "Confirm Password",
                      classNames: "labelText",
                    }}
                    inputWidth={TextInputWidth.fill}
                    classNames="inputTextField"
                    size={TextInputSize.Large}
                    iconButtonProps={{
                      iconProps: {
                        path: chkPasswordFieldIcon(showConfirmPassword),
                      },
                      onClick: () => {
                        setShowConfirmPassword(!showConfirmPassword);
                      },
                    }}
                    alignIcon={TextInputIconAlign.Right}
                    onChange={(e: any) => {
                      setConfirmPassword(e.target.value);
                    }}
                    status={chkForError(errorMsg.confirmPassword)}
                  />
                  {errorMsg.confirmPassword && (
                    <span className="errorMessage">
                      {errorMsg.confirmPassword}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  {/* Continue button with loading state */}
                  <Button
                    text="Submit"
                    variant={ButtonVariant.Secondary}
                    classNames="actionButton"
                    style={actionButtonStyle}
                    onClick={() => {
                      confirmEmail();
                    }}
                    iconProps={
                      showLoader
                        ? { path: IconName.mdiLoading, spin: true }
                        : { path: IconName.mdiArrowRight }
                    }
                    alignIcon={ButtonIconAlign.Right}
                    disabled={showLoader || disableSubmitBtn}
                  />
                </div>
              </div>
            </div>

            {/* Page footer */}
            <Footer />
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Export the VerifyEmail component as the default export
 * This component is used in AppRouter.tsx for the email verification route
 */
export default SetPassword;
